import clsx from 'clsx'
import pluralize from 'pluralize'
import {
  type Dispatch,
  lazy,
  type SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react'
import toast from 'react-hot-toast'
import { Navigate, useParams } from 'react-router'

import { FvButton, OlarkHelper, useFvNavigate } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { supportMessage } from '@/constants'
import { HasFeature, HasPermission } from '@/features/auth'
import { useRole } from '@/features/auth/hooks'
import { useQuoteRequest } from '@/features/quote-request/useQuoteRequest'
import { isSpotQuotingLocked } from '@/features/route-guides/funcs'
import { useRouteGuideView } from '@/features/route-guides/hooks'
import { RouteGuideBlockedBanner } from '@/features/route-guides/RouteGuideBlockedBanner'
import { RouteGuideQuoteSection } from '@/features/route-guides/RouteGuideQuoteSection'
import { SaltBanner } from '@/features/salt/SaltBanner'
import { SpotQuotePanel } from '@/features/spot-quote/SpotQuotePanel'
import {
  type QuoteRequestResponses,
  useQuoteRequestResponses,
} from '@/hooks/shipments/useQuoteRequestResponses'
import { useQuoteRequestTemplate } from '@/hooks/shipments/useRepeatShipment'
import { useSpotQuoteRecipients } from '@/hooks/spotQuotes'
import { useHomePage, useLoadRatesPage, useQuotePage } from '@/route-hooks'
import { routes } from '@/routes'

import InfoBox from '../InfoBox'
import { Loading } from '../Loading'
import { LoadSummary } from '../LoadSummary/LoadSummary'
import { ResponseList } from '../ResponseList'
import { TruckloadAwardConfirmation } from '../TruckloadAwardConfirmation'

export type ListStatus = keyof QuoteRequestResponses

const MiniMessageApp = lazy(() => import('@/features/messages/MiniMessageApp'))

type Props = {
  loads: FullShipment[]
  quoteRequestId?: string
}

const RequestDetail = ({ loads, quoteRequestId }: Props) => {
  const navigate = useFvNavigate()
  const [isAwardingCarrier, setAwardingCarrier] = useState(false)
  const [isMessagesPanelOpen, setMessagesPanelOpen] = useState(false)
  const [truckIndex, setTruckIndex] = useState(0)
  const selectTruck = useCallback((index: number) => setTruckIndex(index), [])
  const quotePage = useQuotePage()

  const activeLoad = loads?.[truckIndex]
  const loadId = activeLoad?.loadId
  const recipientsQuery = useSpotQuoteRecipients(loadId)
  const responses = useQuoteRequestResponses({
    includePendingApiRates: true,
    load: activeLoad,
  })

  const [filter, setFilter] = useState<ListStatus>('active')
  const [listState, setListState] = useListState({
    filter,
    loadId,
    loads,
    responses,
  })

  const activeId = loadId ? listState[loadId]?.[filter] : ''
  const activeData = responses[filter].find(r => r.id === activeId)
  const spotQuotingLocked = isSpotQuotingLocked(activeLoad)
  const routeGuideView = useRouteGuideView(activeLoad)
  // Don't flash the spot quote list before the route guide resolves.
  const isRouteGuideLoading = routeGuideView.isLoading
  const [routeGuideCarrierId, setRouteGuideCarrierId] = useState<string>()
  const routeGuideExecution = routeGuideView.execution
  const selectedStep =
    routeGuideExecution?.steps.find(s => s.carrierId === routeGuideCarrierId) ??
    (spotQuotingLocked
      ? routeGuideExecution?.steps[routeGuideExecution.currentStepIndex]
      : undefined)
  const messageCarrier = selectedStep
    ? { carrierId: selectedStep.carrierId, name: selectedStep.carrierName }
    : activeData
  const hasAwardedEquipment =
    loads?.some(
      load =>
        (load.status === 'awarded' && load.routeGuide?.status !== 'running') ||
        load.status === 'confirmed',
    ) ?? false

  const recipientUserId = recipientsQuery.data?.find(
    r => r.carrierId === messageCarrier?.carrierId,
  )?.userId
  const loadsToBook = loads.filter(l => l.status === 'pending')

  // Don't count quotes that have already been awarded
  const numberOfSelectedCarriers = Object.keys(listState).filter(loadId => {
    const currentLoad = loads?.find(load => load.loadId === loadId)

    return (
      currentLoad?.status !== 'awarded' &&
      currentLoad?.status !== 'confirmed' &&
      listState[loadId]?.selected
    )
  }).length
  const hasCarrierResponses = Object.values(responses).some(
    responseList => responseList.length > 0,
  )

  // Multi-truck requests stay put so the remaining trucks are still reachable.
  if (
    loads?.length === 1 &&
    activeLoad?.routeGuide?.status === 'awarded' &&
    activeLoad.status === 'confirmed'
  ) {
    return <Navigate to={routes.details(activeLoad.loadId)} replace />
  }

  return (
    <HasFeature name="truckLoad" redirect={quotePage}>
      <LoadSummary
        activeIndex={truckIndex}
        hasAwardedEquipment={hasAwardedEquipment}
        load={activeLoad}
        numberOfTrucks={loads?.length ?? 0}
        selectTruck={selectTruck}
      />

      <div className="flex flex-col overflow-auto b1450:col-span-2 bg-fv-gray-50">
        <RouteGuideBlockedBanner className="p-6" load={activeLoad} />

        <SaltBanner loadId={loadId} />
        <div className="flex shrink grow flex-col overflow-auto">
          {isRouteGuideLoading ? (
            <div className="p-6">
              <InfoBox icon="spinner" className="mb-0">
                Loading...
              </InfoBox>
            </div>
          ) : (
            <>
              <RouteGuideQuoteSection
                load={activeLoad}
                view={routeGuideView}
                selectedCarrierId={selectedStep?.carrierId}
                onStepClick={step => {
                  setRouteGuideCarrierId(step.carrierId)
                  setMessagesPanelOpen(true)
                }}
              />

              {!spotQuotingLocked && (
                <>
                  {loadId && (
                    <ResponseList
                      filter={filter}
                      listState={listState}
                      loadId={loadId}
                      loads={loads ?? []}
                      onMessageClick={() => setMessagesPanelOpen(true)}
                      onRowClick={() => setRouteGuideCarrierId(undefined)}
                      responses={responses}
                      selectTruck={selectTruck}
                      setFilter={setFilter}
                      setListState={setListState}
                    />
                  )}

                  <div className="flex flex-initial flex-nowrap items-center bg-fv-gray-50 px-8 py-7">
                    {hasCarrierResponses && (
                      <HasPermission name="load-booker">
                        <FvButton
                          className="ml-auto"
                          theme="primary"
                          fwd
                          disabled={numberOfSelectedCarriers < 1}
                          onClick={() => setAwardingCarrier(true)}
                          icon="trophy"
                          transform="up-1"
                        >
                          <span>
                            Award selected{' '}
                            {pluralize(
                              'carrier',
                              numberOfSelectedCarriers > 0
                                ? numberOfSelectedCarriers
                                : (loads?.length ?? 0),
                            )}
                          </span>
                        </FvButton>
                      </HasPermission>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <HasPermission name="load-creator">
          {!spotQuotingLocked && !isRouteGuideLoading && (
            <SpotQuotePanel
              activeLoadIndex={truckIndex}
              defaultOpen
              label="Send out for more quotes via email"
              loads={loads}
              onSuccess={() => setFilter('pending')}
              selectTruck={selectTruck}
            />
          )}
        </HasPermission>
      </div>

      <button
        aria-label="Close messages panel"
        className={clsx(
          'hidden b1450:block b1450:fixed b1450:inset-0 b1450:z-[100] b1450:bg-black b1450:transition-opacity b1450:duration-300 b1450:ease-in-out',
          isMessagesPanelOpen
            ? 'b1450:opacity-60 b1450:pointer-events-auto'
            : 'b1450:opacity-0 b1450:pointer-events-none',
        )}
        disabled={!isMessagesPanelOpen}
        onClick={() => setMessagesPanelOpen(false)}
        type="button"
      />

      <div
        className={clsx(
          'b1450:fixed b1450:top-0 b1450:right-0 b1450:block b1450:h-dvh b1450:w-[30rem] b1450:z-[110] p-6 overflow-auto bg-fv-gray-100 border-l border-fv-gray b1450:shadow-lg b1450:transition-transform b1450:duration-300 b1450:ease-in-out',
          isMessagesPanelOpen
            ? 'b1450:translate-x-0 b1450:visible'
            : 'b1450:translate-x-full b1450:invisible',
        )}
      >
        {messageCarrier && loadId ? (
          <>
            {!selectedStep && activeData?.status === 'error' && (
              <InfoBox icon="exclamation-triangle">
                {activeData.errors?.map((e: any) => e.message).join(', ') ??
                  'Unknown error, please contact support.'}
              </InfoBox>
            )}

            <h6 className="mb-4">{messageCarrier.name}</h6>

            <Suspense fallback={<Loading />}>
              <MiniMessageApp
                loadId={loadId}
                carrierId={messageCarrier.carrierId}
                carrierName={messageCarrier.name}
                userId={recipientUserId}
              />
            </Suspense>
          </>
        ) : (
          <InfoBox>
            We don't have any messages to show right now. Once a carrier starts
            bidding or communicating, their messages will appear here.
          </InfoBox>
        )}
      </div>

      {quoteRequestId && (
        <TruckloadAwardConfirmation
          loadsToBook={loads.map(l => ({
            ...l,
            quoteId: listState[l.loadId]?.selected,
          }))}
          isOpen={isAwardingCarrier}
          onClose={() => setAwardingCarrier(false)}
          onSubmit={() => {
            const selectedQuotes = Object.fromEntries(
              loadsToBook
                .filter(l => listState[l.loadId]?.selected)
                .map(l => [l.loadId, listState[l.loadId]?.selected]),
            )
            navigate(
              routes.bookTruckload({
                quoteRequestId,
                selectedQuotes,
              }),
            )
          }}
          showUnawarded={true}
        />
      )}
    </HasFeature>
  )
}

export const QuoteRequestDetail = () => {
  const { quoteRequestId } = useParams<{ quoteRequestId: string }>()
  const role = useRole()
  const quoteRequestQuery = useQuoteRequest(quoteRequestId)
  const homePage = useHomePage()
  const navigate = useFvNavigate()
  const isError = quoteRequestQuery.isError
  const isLoading = quoteRequestQuery.isLoading
  const loads = quoteRequestQuery.data
  const ratesPage = useLoadRatesPage()
  const [, setTemplate] = useQuoteRequestTemplate()

  useEffect(() => {
    if (isLoading || loads?.length) return
    toast.error('No shipments found on quote request.')
    navigate('/pending')
  }, [isLoading, loads?.length, navigate])

  useEffect(() => {
    if (!loads?.length) return
    // Clicking "Start over" in `LoadSummary` cancels the
    // quote request and removes all shipments from the DB.
    // Setting the quote request in localStorage to access
    // from the quote page instead of trying to refetch it.
    setTemplate(loads)
  }, [loads, setTemplate])

  if (role === 'vendor') {
    if (!loads?.length) return <Loading />
    return <Navigate to={ratesPage(loads[0])} replace />
  }

  return (
    <HasFeature name="truckLoad" redirect={homePage}>
      {!loads?.length ? (
        <>
          <LoadSummary
            activeIndex={0}
            numberOfTrucks={0}
            selectTruck={() => null}
          />
          <div className="p-6">
            {isError ? (
              <InfoBox icon="ban">
                Unable to load quote request, {supportMessage}
              </InfoBox>
            ) : (
              <InfoBox icon="spinner">Loading...</InfoBox>
            )}
          </div>
          <div className="messages-ctn" />
        </>
      ) : (
        <>
          <RequestDetail loads={loads} quoteRequestId={quoteRequestId} />
          <OlarkHelper loadId={loads?.[0]?.loadId} />
        </>
      )}
    </HasFeature>
  )
}

export type ListState = {
  [key: string]: {
    active: string
    declined: string
    pending: string
    selected: string
  }
}

function useListState(opts: {
  filter: ListStatus
  loadId?: string
  loads?: FullShipment[]
  responses: QuoteRequestResponses
}): [ListState, Dispatch<SetStateAction<ListState>>] {
  const [listState, setListState] = useState<ListState>({})
  const { filter, loadId, loads, responses } = opts

  // Initialize state after fetching loads
  useEffect(() => {
    if (Object.keys(listState).length > 0 || !loads) return

    const initialState: ListState = {}

    loads.forEach(load => {
      initialState[load.loadId] = {
        active: '',
        declined: '',
        pending: '',
        selected: load.selectedQuote?._id ?? '',
      }
    })

    setListState(initialState)
  }, [listState, loads])

  // Ensure active row in each filtered view
  useEffect(() => {
    if (!loadId) return

    setListState(prev => {
      const activeId = prev[loadId]?.[filter]

      if (activeId) {
        // Check that `activeId` is in the list
        const activeData = responses[filter].find(d => d.id === activeId)
        if (activeData) return prev
      }

      const firstData = responses[filter][0]

      return {
        ...prev,
        [loadId]: {
          ...prev[loadId],
          [filter]: firstData?.id ?? '',
        },
      }
    })
  }, [loadId, filter, responses])

  return [listState, setListState]
}
